import React from "react";
import { StyleSheet, View } from "react-native";
import { Image } from "expo-image";
import { CustomAlert } from "@/components/ThemedAlert";
import { Collapsible } from "@components/ui/CollapsibleView";
import { ExternalLink } from "@/components/ExternalLink";
import { ThemedText } from "@/components/ThemedText";
import ThemedScreen from "@/components/ThemedScreen";
import { router } from "expo-router";
import { ThemedPanel } from "@/components/ThemedPanel";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Animated Header & Scroll Workbench Screen.
 * Demonstrates scroll-driven header opacity transitions, collapsible sections,
 * and high-density content scrolling behavior.
 */
export default function AnimatedHeaderScreen() {
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");

  return (
    <ThemedScreen
      title="Animated Header Workbench"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
      rightIcon={{
        name: "search",
        onPress: () => CustomAlert.alert("Search Trigger", "Animated header search action activated."),
      }}
    >
      <View style={styles.container}>
        <ThemedPanel padding={14} elevation="low" style={styles.introPanel}>
          <ThemedText type="defaultSemiBold" style={styles.introTitle}>
            Scroll Behavior & Header Transitions
          </ThemedText>
          <ThemedText style={{ color: textMuted, marginTop: 4 }}>
            Scroll down to inspect header background interpolation, border opacity, and sticky element alignment.
          </ThemedText>
        </ThemedPanel>

        <Collapsible title="General Information">
          <ThemedText style={styles.collapsibleContent}>
            ThemedScreen dynamically measures and computes scroll offsets, smoothly transitioning the header
            background color and elevation as content passes under the navigation bar.
          </ThemedText>
        </Collapsible>

        <Collapsible title="Static Media & Densities">
          <View style={styles.collapsibleContent}>
            <ThemedText>
              For static images, React Native provides screen density resolution using @2x and @3x suffixes.
            </ThemedText>
            <Image
              source={require("@assets/images/memories.png")}
              style={styles.sampleImage}
              contentFit="contain"
            />
            <ExternalLink href="https://reactnative.dev/docs/images">
              <ThemedText type="link" style={{ marginTop: 8 }}>
                Learn more about React Native image assets
              </ThemedText>
            </ExternalLink>
          </View>
        </Collapsible>

        <Collapsible title="High-Density Scroll Rows (100 Items)">
          <View style={styles.collapsibleContent}>
            {[...Array(50)].map((_, i) => (
              <View key={i} style={[styles.sampleRow, { backgroundColor: badgeBg }]}>
                <ThemedText type="defaultSemiBold" style={{ color: tintColor }}>
                  Specimen Item #{i + 1}
                </ThemedText>
                <ThemedText style={{ color: textMuted, fontSize: 12 }}>
                  Offset test row for inertial scroll validation
                </ThemedText>
              </View>
            ))}
          </View>
        </Collapsible>
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 40,
    gap: 12,
  },
  introPanel: {
    marginBottom: 8,
  },
  introTitle: {
    fontSize: 16,
  },
  collapsibleContent: {
    paddingVertical: 8,
    gap: 8,
  },
  sampleImage: {
    width: 200,
    height: 80,
    alignSelf: "center",
    marginVertical: 12,
  },
  sampleRow: {
    padding: 10,
    marginBottom: 6,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
});
